import { api } from '../api';

/**
 * お気に入りキーワード管理API
 */
class FavoriteKeywordsAPI {
  /**
   * お気に入りキーワード一覧を取得
   * @param {Object} params - クエリパラメータ
   * @param {number} params.skip - スキップ件数
   * @param {number} params.limit - 取得件数
   * @param {boolean} params.includeAnalysisInfo - 既存分析情報を含めるか
   * @returns {Promise<Object>} お気に入りキーワード一覧
   */
  async getFavoriteKeywords(params = {}) {
    const { skip = 0, limit = 100, includeAnalysisInfo = true } = params;

    const response = await api.get('/api/user/favorite-keywords', {
      params: {
        skip,
        limit,
        include_analysis_info: includeAnalysisInfo,
      },
    });

    return response.data;
  }

  /**
   * お気に入りキーワードを追加
   * @param {Object} keywordData - キーワードデータ
   * @param {string} keywordData.keyword - キーワード
   * @param {number} keywordData.searchVolume - 月間検索ボリューム
   * @param {number} keywordData.keywordDifficulty - キーワード難易度
   * @param {number} keywordData.cpc - クリック単価
   * @param {string} keywordData.sourceAnalysisId - 追加元の分析ID
   * @returns {Promise<Object>} 追加されたキーワード情報
   */
  async addFavoriteKeyword(keywordData) {
    const payload = {
      keyword: keywordData.keyword,
      search_volume: keywordData.searchVolume,
      keyword_difficulty: keywordData.keywordDifficulty,
      cpc: keywordData.cpc,
      source_analysis_id: keywordData.sourceAnalysisId,
    };

    const response = await api.post('/api/user/favorite-keywords', payload);
    return response.data;
  }

  /**
   * 特定のお気に入りキーワード情報を取得
   * @param {string} keyword - キーワード
   * @returns {Promise<Object>} キーワード情報
   */
  async getFavoriteKeyword(keyword) {
    const response = await api.get(
      `/api/user/favorite-keywords/${encodeURIComponent(keyword)}`,
    );
    return response.data;
  }

  /**
   * お気に入りキーワードのメトリクスを更新
   * @param {string} keyword - キーワード
   * @param {Object} updateData - 更新データ
   * @returns {Promise<Object>} 更新されたキーワード情報
   */
  async updateFavoriteKeyword(keyword, updateData) {
    const payload = {
      search_volume: updateData.searchVolume,
      keyword_difficulty: updateData.keywordDifficulty,
      cpc: updateData.cpc,
    };

    const response = await api.put(
      `/api/user/favorite-keywords/${encodeURIComponent(keyword)}`,
      payload,
    );
    return response.data;
  }

  /**
   * お気に入りキーワードを削除
   * @param {string} keyword - キーワード
   * @returns {Promise<Object>} 削除結果
   */
  async deleteFavoriteKeyword(keyword) {
    const response = await api.delete(
      `/api/user/favorite-keywords/${encodeURIComponent(keyword)}`,
    );
    return response.data;
  }

  /**
   * キーワードの分析履歴を取得
   * @param {string} keyword - キーワード
   * @param {number} limit - 取得件数
   * @returns {Promise<Object>} 分析履歴
   */
  async getKeywordAnalysisHistory(keyword, limit = 10) {
    const response = await api.get(
      `/api/user/favorite-keywords/${encodeURIComponent(keyword)}/analyses`,
      {
        params: { limit },
      },
    );
    return response.data;
  }

  /**
   * お気に入りキーワードで新規分析を開始
   * @param {string} keyword - キーワード
   * @returns {Promise<Object>} 分析開始結果
   */
  async analyzeKeyword(keyword) {
    const response = await api.post(
      `/api/user/favorite-keywords/${encodeURIComponent(keyword)}/analyze`,
    );
    return response.data;
  }

  /**
   * キーワードがお気に入りに登録されているかチェック
   * @param {string} keyword - キーワード
   * @returns {Promise<boolean>} お気に入り登録状況
   */
  async isFavoriteKeyword(keyword) {
    try {
      await this.getFavoriteKeyword(keyword);
      return true;
    } catch (error) {
      if (error.response?.status === 404) {
        return false;
      }
      throw error;
    }
  }
}

export default new FavoriteKeywordsAPI();
